import React, { useState, useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import SetlistItem from './SetlistItem';
import SideActionPanel from './SideActionPanel';
import SongPartPreview from '../../../../../components/songs/SongPartPreview';
import { hasPart, hasComment } from '../../utils/eventHelpers';
import { fetchSongById } from '../../../../../services/songService';

// New component for comment preview modal
const CommentPreviewModal = ({ show, onHide, songTitle, comment }) => {
  const { t } = useLanguage();
  return (
    <Modal show={show} onHide={onHide} centered size="sm">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1rem' }}>{t('events.setlist.comment')} - {songTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p style={{ whiteSpace: 'pre-wrap' }}>{comment}</p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide}>{t('common.close')}</Button>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Setlist tab content component
 * 
 * @param {object} setlist - Setlist data
 * @param {object} songsWithParts - Map of songs with their part info (optional)
 * @param {boolean} canEdit - Whether the user can edit the setlist
 * @param {boolean} loadingSetlist - Whether the setlist is loading
 * @param {function} onSongClick - Function to handle song click
 * @param {function} onSongOptionsClick - Function to handle song options click
 * @param {function} onEditSetlist - Function to open setlist edit modal
 * @param {function} onMoveSong - Function to handle song reordering
 * @param {function} onDeleteSong - Function to delete song
 * @param {function} onEditRole - Function to edit song role
 * @param {function} onSaveSetlist - Function to save setlist changes directly
 * @returns {JSX.Element} Setlist view component
 */
const SetlistView = ({
  setlist,
  songsWithParts = {},
  canEdit,
  loadingSetlist,
  onSongClick,
  onSongOptionsClick,
  onEditSetlist,
  onMoveSong,
  onDeleteSong,
  onEditRole,
  onSaveSetlist
}) => {
  const { t } = useLanguage();
  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [selectedSongIds, setSelectedSongIds] = useState([]);
  
  // Song part preview state
  const [selectedSong, setSelectedSong] = useState(null);
  const [showPartPreview, setShowPartPreview] = useState(false);
  const [loadingSongParts, setLoadingSongParts] = useState(false);
  const [localSongsWithParts, setLocalSongsWithParts] = useState(songsWithParts || {});

  // Comment preview state
  const [showCommentPreview, setShowCommentPreview] = useState(false);
  const [commentPreviewSong, setCommentPreviewSong] = useState(null);

  // Saving state
  const [isSaving, setIsSaving] = useState(false);

  // Setlist ID to track setlist changes
  const [currentSetlistId, setCurrentSetlistId] = useState(setlist?.id);

  // Update local songs with parts when props change
  useEffect(() => {
    if (songsWithParts && Object.keys(songsWithParts).length > 0) {
      setLocalSongsWithParts(songsWithParts);
    }
  }, [songsWithParts]);

  // Only reset edit mode when the setlist ID changes (completely new setlist)
  useEffect(() => {
    if (setlist?.id !== currentSetlistId) {
      // Only reset if we have a completely different setlist
      if (editMode) {
        setEditMode(false);
        setSelectedSongIds([]);
      }
      // Update current setlist ID
      setCurrentSetlistId(setlist?.id);
    }
  }, [setlist?.id, currentSetlistId, editMode]);

  // Handle song selection in edit mode - single selection only
  const handleSongSelect = (songId) => {
    if (selectedSongIds.includes(songId)) {
      // Deselect if already selected
      setSelectedSongIds([]);
    } else {
      // Select only this song (replace any previous selection)
      setSelectedSongIds([songId]);
    }
  };

  // Handle song click to view part file
  const handleSongItemClick = async (songId) => {
    if (editMode) return; // Don't handle clicks in edit mode
    
    // Try to get song data from props first
    let songData = localSongsWithParts[songId];
    
    // If not available, fetch it
    if (!songData || !songData.has_part_file) {
      try {
        setLoadingSongParts(true);
        const fetchedSongData = await fetchSongById(songId);
        
        if (fetchedSongData) {
          // Find the song in the setlist to get the comment
          const setlistSong = setlist.songs.find(s => s.song_id === songId);
          
          // Create full song data
          songData = {
            ...fetchedSongData,
            ...setlistSong, // Include setlist song data (song_role, comment)
            id: songId // Important for SongPartPreview
          };
          
          // Update local cache
          setLocalSongsWithParts(prev => ({
            ...prev,
            [songId]: songData
          }));
        }
      } catch (error) {
      } finally {
        setLoadingSongParts(false);
      }
    } else {
      // Make sure song has the proper id for SongPartPreview
      songData = {
        ...songData,
        id: songId
      };
      
      // Get comment from setlist if available
      const setlistSong = setlist.songs.find(s => s.song_id === songId);
      if (setlistSong && setlistSong.comment) {
        songData.comment = setlistSong.comment;
      }
    }
    
    // If song has part file, show preview
    if (songData && songData.has_part_file) {
      setSelectedSong(songData);
      setShowPartPreview(true);
    } else if (onSongClick) {
      // Otherwise use default click handler if provided
      onSongClick(songId);
    }
  };

  // Handle comment icon click
  const handleCommentClick = (songId) => {
    const song = setlist.songs.find(s => s.song_id === songId);
    if (song && song.comment) {
      // Get full song data from local cache or create a basic version
      const songData = localSongsWithParts[songId] || { ...song, title: song.title };
      setCommentPreviewSong(songData);
      setShowCommentPreview(true);
    }
  };

  // Check if a song has a part file
  const hasSongPart = (songId) => {
    const song = localSongsWithParts[songId];
    return song && song.has_part_file;
  };

  // Handle bulk delete - FIXED VERSION
const handleBulkDelete = (songIds) => {
  if (window.confirm(t('events.setlist.bulkDeleteConfirm', { count: songIds.length }))) {
    // Delete each song, skipping individual confirmations
    songIds.forEach(songId => onDeleteSong(songId, null, true)); // true = skip confirmation
    // Clear selection
    setSelectedSongIds([]);
  }
};


  // Save setlist changes to backend when exiting edit mode
  const handleExitEditMode = async () => {
    if (!setlist || !onSaveSetlist) {
      setEditMode(false);
      setSelectedSongIds([]);
      return;
    }

    setIsSaving(true);
    
    try {
      
      // Create a local copy of the setlist to work with
      const setlistToSave = { ...setlist };
      
      // Ensure songs have proper order property based on array position
      const songsWithOrder = setlistToSave.songs.map((song, index) => ({
        ...song,
        order: index
      }));
      
      // Create updated setlist with ordered songs
      const orderedSetlist = {
        ...setlistToSave,
        songs: songsWithOrder
      };
      
      // Call the save function passed from parent, but keep existing UI state until save completes
      const savedSetlist = await onSaveSetlist(orderedSetlist);
      
      
      // Exit edit mode
      setEditMode(false);
      
      // Clear selection
      setSelectedSongIds([]);
    } catch (error) {
      // Still exit edit mode even if save fails
      setEditMode(false);
      setSelectedSongIds([]);
    } finally {
      setIsSaving(false);
    }
  };

  // Render setlist header with edit toggle - now with saving state
  const renderSetlistHeader = () => {
    return (
      <div className="setlist-header">
        <div className="setlist-title">{t('events.setlist.title')}</div>
        {canEdit && (
          <button
            className={`edit-mode-toggle ${editMode ? 'active' : ''}`}
            onClick={() => {
              if (editMode) {
                // Now saves automatically when exiting edit mode
                handleExitEditMode();
              } else {
                setEditMode(true);
              }
            }}
            disabled={isSaving}
          >
            {editMode
              ? (isSaving ? t('events.setlist.saving') : t('events.setlist.finishEditing'))
              : t('events.setlist.editSetlist')}
          </button>
        )}
      </div>
    );
  };

  // Loading state
  if (loadingSetlist) {
    return (
      <div className="event-section-content text-center py-3">
        <div className="spinner-border spinner-border-sm text-primary" role="status">
          <span className="visually-hidden">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  // Empty state - UPDATED to fix the layout issue
  if (!setlist || !setlist.songs || setlist.songs.length === 0) {
    return (
      <div className="event-section-content empty-content">
        <div className="empty-setlist-container">
          <p className="text-muted empty-program-text">{t('events.setlist.empty')}</p>
          {canEdit && (
            <Button variant="primary" size="sm" onClick={onEditSetlist} className="add-song-btn">
              {t('events.setlist.addSong')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`event-section-content ${editMode ? 'edit-mode-active' : ''}`}>
      {renderSetlistHeader()}
      
      <div className={`event-setlist ${editMode ? 'edit-mode' : ''}`}>
        {setlist.songs.map((song) => {
          // Get additional song data if available
          const songWithData = localSongsWithParts[song.song_id] || {};
          
          // Merge setlist song with full song data
          const fullSong = {
            ...song,
            key: songWithData.key,
            tempo: songWithData.tempo
          };
          
          return (
            <SetlistItem
              key={`${song.song_id}-${Date.now()}`} // Ensure re-rendering on updates
              song={fullSong}
              isSelected={selectedSongIds.includes(song.song_id)}
              editMode={editMode}
              onSelect={handleSongSelect}
              onClick={() => handleSongItemClick(song.song_id)}
              onOptionsClick={onSongOptionsClick}
              onCommentClick={handleCommentClick}
              hasPart={hasSongPart(song.song_id)}
              hasComment={!!song.comment}
            />
          );
        })}
      </div>
      
      {/* "Add song" button when in edit mode */}
      {editMode && (
        <div className="setlist-edit-actions">
          <Button variant="primary" size="sm" onClick={onEditSetlist}>
            {t('events.setlist.addSong')}
          </Button>
        </div>
      )}
      
      {/* Side action panel for edit mode */}
      {editMode && (
        <SideActionPanel
          selectedSongIds={selectedSongIds}
          setlistSongs={setlist.songs}
          onMoveUp={(songId) => onMoveSong(songId, 'up')}
          onMoveDown={(songId) => onMoveSong(songId, 'down')}
          onEditRole={(song) => onEditRole(song)}
          onDelete={handleBulkDelete}
        />
      )}
      
      {/* Song Part Preview Modal */}
      {showPartPreview && selectedSong && (
        <SongPartPreview 
          show={showPartPreview}
          onHide={() => setShowPartPreview(false)}
          song={selectedSong}
        />
      )}
      
      {/* Comment Preview Modal */}
      {showCommentPreview && commentPreviewSong && (
        <CommentPreviewModal
          show={showCommentPreview}
          onHide={() => setShowCommentPreview(false)}
          songTitle={commentPreviewSong.title}
          comment={commentPreviewSong.comment}
        />
      )}
    </div>
  );
};

export default SetlistView;